/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import * as React from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Headline, Text, TextLink } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import type { HeadlineVariant } from '@oneaudi/unified-web-components/dist/cjs/components/headline';
import { messages } from '../../../i18n/messages';
import { isTranslationLoaded } from '../../../lib/isTranslationLoaded';
import ImprintModal from '../ImprintModal';
import { ButtonAlignedLeft } from '../PartnerBusinessCardAddress';
import { PartnerContext } from '../../PartnerProvider';
import { useTrackingManager } from '../../useTrackingManager';
import type { HeadlineOrder, HeadlineType, VtpDealer } from '../PartnerBusinessCard';
import { createDirectionsLink } from '../PartnerLinks';

interface Props {
  vtpData: {
    imprint?: string;
    vtpDealerResultsURL?: string;
    registrationNumber?: string;
    vtpDealer?: VtpDealer | null;
    locale: string;
  };
  partnerName: string;
  pbcRef: React.RefObject<HTMLDialogElement>;
  isGoogleMapLink?: boolean;
  partnerAddress: React.JSX.Element[];
  headline: {
    headlineTag?: HeadlineType;
    headlineOrder?: HeadlineOrder;
  };
}

const GoogleButton = styled(TextLink)`
  text-decoration: none;
`;

const CustomButton = styled(ButtonAlignedLeft)`
  margin: 0 0 16px 0;
`;

const BevAgencyDealerContainer = styled.div`
  display: grid;
  .bev-agency-label {
    margin: 0 0 16px 0;
  }
`;

export const BevAgencyDealer: React.FC<Props> = ({
  vtpData,
  partnerName,
  pbcRef,
  isGoogleMapLink,
  partnerAddress,
  headline,
}): React.ReactElement => {
  const [openDialog, setIsOpen] = React.useState(false);
  const { partner } = React.useContext(PartnerContext);
  const trackingManager = useTrackingManager();
  const { vtpDealer, locale } = vtpData;
  const { name = '', street = '', zipCode = '', city = '' } = vtpDealer || {};
  const dealerAddress = vtpDealer ? `${street}, ${zipCode} ${city}` : '';
  const dealerDirectionUrl = vtpDealer
    ? createDirectionsLink({ ...vtpDealer, locale, address: dealerAddress })
    : '';
  const { headlineOrder = '3', headlineTag = 'h2' } = headline;

  const vtpTitle = useI18n(messages.vtpHeadline);
  const showTitle = isTranslationLoaded(messages.vtpHeadline.id, vtpTitle);
  const nameVariant = (() => {
    if (showTitle) return { type: 'headline', order: '4', weight: 'normal' };
    return { type: 'headline', order: headlineOrder, weight: 'normal' };
  })() as HeadlineVariant;

  const nameAs = (() => {
    if (showTitle) return 'div';
    return headlineTag;
  })();

  const registrationNumber = useI18n(messages.registrationNumber);
  const imprintMessage = useI18n(messages.imprintMessage);

  return (
    <BevAgencyDealerContainer>
      <Headline
        tag={nameAs}
        variant={nameVariant}
        data-testid="pbc-vtp-bev-agency-name"
        className="bev-agency-label"
      >
        {name || partner.officialName || partner.nonOfficialName}
      </Headline>
      {vtpData?.registrationNumber && (
        <Text
          variant={{ type: 'copy', order: '2', style: 'normal' }}
          margin={[0, 0, 'var(--spacing-relative-lg)', 0]}
        >
          {registrationNumber} {vtpData?.registrationNumber}
        </Text>
      )}
      {isGoogleMapLink && vtpDealer && (
        <GoogleButton
          variant="standalone"
          icon="geofence"
          iconPosition="leading"
          size="md"
          margin={['var(--spacing-relative-sm)', 0, 'var(--spacing-relative-lg)', 0]}
          href={dealerDirectionUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackingManager.click(
              'exit_link',
              dealerDirectionUrl,
              'link',
              dealerAddress,
              name || partnerName,
              '1',
              'location',
            );
          }}
          data-testid="pbc-vtp-directions"
        >
          {dealerAddress || partnerAddress}
        </GoogleButton>
      )}

      {vtpData?.imprint && (
        <>
          <CustomButton
            variant="standalone"
            icon="address-book"
            iconPosition="leading"
            size="md"
            onClick={() => {
              setIsOpen(!openDialog);
            }}
            data-testid="pbc-imprint-trigger"
          >
            {imprintMessage}
          </CustomButton>
          <ImprintModal
            open={openDialog}
            close={() => {
              setIsOpen(!openDialog);
            }}
            imprintRef={pbcRef}
            imprintData={vtpData.imprint}
          />
        </>
      )}
    </BevAgencyDealerContainer>
  );
};
